import { useState, useRef, useEffect, useCallback } from "react";
import adminServices from "../../../../services/adminServices";
import ResponsiveTable from "../../../../components/Table/Responsivetable";

const ACTION_OPTIONS = [
  "All Actions",
  "PLAN_ASSIGNED_TO_TENANT",
  "PLAN_UNASSIGNED_FROM_TENANT",
  "TENANT_UPDATED",
  "CONTRACT_UPDATED",
  "LEARNER_CREATED",
];

const ACTION_COLORS = {
  PLAN_ASSIGNED_TO_TENANT:     "text-blue-600 bg-blue-50",
  PLAN_UNASSIGNED_FROM_TENANT: "text-gray-500 bg-gray-100",
  TENANT_UPDATED:              "text-orange-500 bg-orange-50",
  CONTRACT_UPDATED:            "text-orange-500 bg-orange-50",
  LEARNER_CREATED:             "text-green-600 bg-green-50",
};

function fmt(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
}

function detailsText(details) {
  if (!details) return "—";
  if (typeof details === "string") return details;
  return Object.entries(details)
    .map(([k, v]) => `${k}: ${v}`)
    .join(", ");
}

export default function AuditHistory({ clientDetails }) {
  const clientId = clientDetails?.id;

  const [fromDate,       setFromDate]       = useState("");
  const [toDate,         setToDate]         = useState("");
  const [selectedAction, setSelectedAction] = useState("All Actions");
  const [dropdownOpen,   setDropdownOpen]   = useState(false);
  const dropdownRef = useRef(null);

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const PAGE_SIZE = 20;

  const load = useCallback(async (p = 0) => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (selectedAction !== "All Actions") params.action = selectedAction;
      if (fromDate) params.fromDate = fromDate;
      if (toDate)   params.toDate   = toDate;
      const res = await adminServices.getClientAuditLog(clientId, params);
      if (res.ok) {
        setRows(res.data?.data  || []);
        setTotal(res.data?.total || 0);
      } else {
        setError(res.data?.message || "Failed to load audit log.");
      }
    } catch {
      setError("Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedAction, fromDate, toDate]);

  useEffect(() => { setPage(0); load(0); }, [clientId, selectedAction, fromDate, toDate]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleExportCSV = () => {
    const headers = ["Timestamp", "Actor", "Action", "Entity", "Details"];
    const csvRows = rows.map(r => [
      fmt(r.createdAt), r.actor || "—", r.action, r.entityType || "—", detailsText(r.details),
    ]);
    const csv = [headers, ...csvRows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "audit-log.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      key: "createdAt",
      label: "Timestamp",
      render: (row) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">{fmt(row.createdAt)}</span>
      ),
    },
    {
      key: "actor",
      label: "Actor",
      render: (row) => (
        <span className="text-xs font-semibold text-gray-800 whitespace-nowrap">{row.actor || "—"}</span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (row) => {
        const cls = ACTION_COLORS[row.action] || "text-gray-500 bg-gray-100";
        return (
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium tracking-wide ${cls}`}>
            {row.action}
          </span>
        );
      },
    },
    {
      key: "entityType",
      label: "Entity",
      render: (row) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">{row.entityType || "—"}</span>
      ),
    },
    {
      key: "details",
      label: "Details",
      render: (row) => (
        <span className="text-xs text-gray-400 max-w-xs truncate block" title={detailsText(row.details)}>
          {detailsText(row.details)}
        </span>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen p-4">
      <div className="bg-white rounded-lg border border-gray-200">

        {/* ── Filters Bar ── */}
        <div className="relative z-10 flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
          <input
            type="date" value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <input
            type="date" value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(p => !p)}
              className="flex items-center gap-6 border border-gray-300 rounded px-3 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[200px] justify-between"
            >
              <span>{selectedAction}</span>
              <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {dropdownOpen && (
              <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg min-w-[220px]">
                {ACTION_OPTIONS.map(option => (
                  <button
                    key={option}
                    onClick={() => { setSelectedAction(option); setDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition-colors ${
                      selectedAction === option ? "bg-blue-600 text-white hover:bg-blue-600" : "text-gray-700"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto">
            <button
              onClick={handleExportCSV}
              disabled={rows.length === 0}
              className="border border-gray-300 rounded px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <ResponsiveTable
          columns={columns}
          data={rows}
          loading={loading}
        />

        {/* ── Pagination ── */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {total === 0 ? "No results" : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={page === 0 || loading}
                onClick={() => { const p = page - 1; setPage(p); load(p); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
              >
                ‹
              </button>
              <span className="text-xs text-gray-600 px-2 font-medium">{page + 1} / {totalPages}</span>
              <button
                disabled={page >= totalPages - 1 || loading}
                onClick={() => { const p = page + 1; setPage(p); load(p); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
              >
                ›
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}